import { Grid } from '../core/Grid.js';
import { Position, Point, Direction, parsePositionKey, positionKey } from '../core/GridGeometry.js';
import { Renderer, RenderConfig, COLORS, colorWithAlpha, canvasFont } from './Renderer.js';
import { IVortexGame } from '../games/IVortexGame.js';
import type { IGame } from '../games/GameRegistry.js';
import type { ScoringHighlight } from '../games/scoringRules.js';
import type { FinePoint } from '../input/KeyboardController.js';

/**
 * Quadrant positions for badges relative to cell center.
 * Cardinal directions (N, E, S, W) are centered on that edge.
 * Diagonal directions (NE, SE, SW, NW) are in the corners.
 */
export type BadgeQuadrant = 'N' | 'E' | 'S' | 'W' | 'NE' | 'SE' | 'SW' | 'NW';

/** A straight line piece in canvas (pixel) coordinates. */
export interface CanvasSeg { from: { x: number; y: number }; to: { x: number; y: number }; }

/** Keyboard cursor appearance: crosshair (place), reticle (trace), X (delete). */
export type KeyboardCursorStyle = 'crosshair' | 'reticle' | 'delete';

export class GridRenderer extends Renderer {
    /** First cell of an in-game gate pair being created (Activate Vortex mode). */
    private gatePendingCreateCell: Position | null = null;
    /** Canonical pair ids ("keyA|keyB") that are linked in the Hub (drawn solid). */
    private linkedGatePairIds: Set<string> = new Set();
    /** Keyboard-mode cursor cell, or null when keyboard mode is off. */
    private keyboardCursor: Position | null = null;
    /** Half-grid keyboard cursor (Walls), or null when unused. Takes precedence. */
    private keyboardFineCursor: FinePoint | null = null;
    /** Intersection-point keyboard cursor (Line), or null when unused. */
    private keyboardPointCursor: Position | null = null;
    /** Draw a thick background-coloured backing stroke under the keyboard cursor.
     *  Subclasses opt in when the board behind it is busy enough to swallow a thin
     *  line — Flood's water fill. */
    protected keyboardCursorHalo = false;
    /** The fine cursor's top-rim row is a row of slots, drawn as a rim marker rather
     *  than a crosshair. Mirrors the input's `fineTopRimSlots()` — Flood's faucet. */
    protected keyboardFineTopRimSlots = false;
    /** Draw the fine cursor as an inward rim marker on any of the four rim edges,
     *  for a game whose boundary piece is anchored *to* the rim rather than sitting
     *  on it (Branches' stem). The wider cousin of `keyboardFineTopRimSlots`. */
    protected keyboardFineRimMarkers = false;
    /** On the fine cursor, draw intersections as a wide-gap crosshair (clearing the
     *  digit target circles they sit on) rather than a dot — for Line, whose digits
     *  are targets at the corners. Edges keep the half crosshair. */
    protected keyboardFinePointCursor = false;
    /** The cell/center the last trace step came from — draws a hint segment into
     *  the cursor — or null when not mid-trace. */
    private keyboardCursorHintFrom: Position | null = null;
    private keyboardFineHintFrom: FinePoint | null = null;
    /** Which cursor to draw: crosshair (place), reticle (trace), or X (delete). */
    private keyboardCursorStyle: KeyboardCursorStyle = 'crosshair';

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: Partial<RenderConfig> = {}) {
        super(canvas, grid, config);
    }

    /** Set/clear the pending first gate of an in-game pair creation. */
    setGatePendingCreateCell(pos: Position | null): void {
        this.gatePendingCreateCell = pos;
    }

    /** Tell the renderer which gate pairs are linked (drawn solid vs dashed). */
    setLinkedGatePairIds(ids: Set<string>): void {
        this.linkedGatePairIds = ids;
    }

    /** Set/clear the keyboard-mode cursor cell (null hides it), its style, and the
     *  optional trace-hint origin cell. */
    setKeyboardCursor(pos: Position | null, style: KeyboardCursorStyle = 'crosshair', hintFrom: Position | null = null): void {
        this.keyboardCursor = pos;
        this.keyboardCursorStyle = style;
        this.keyboardCursorHintFrom = hintFrom;
    }

    /** Set/clear the half-grid keyboard cursor (Walls). Takes precedence over the
     *  cell cursor when set. */
    setKeyboardFineCursor(fine: FinePoint | null, style: KeyboardCursorStyle = 'crosshair', hintFrom: FinePoint | null = null): void {
        this.keyboardFineCursor = fine;
        this.keyboardFineHintFrom = hintFrom;
        if (fine) this.keyboardCursorStyle = style;
    }

    /**
     * The fine cursor as a grid intersection, or null when it isn't on one (or the
     * mode is off). A renderer animating a piece that spans two intersections uses
     * this to draw it *the way the gesture went*: a keyboard trace lands the cursor
     * on the end it arrived at, so the piece grows away from that end and retracts
     * into it, instead of following whatever order it happens to be stored in.
     */
    protected keyboardIntersection(): Point | null {
        const fine = this.keyboardFineCursor;
        if (!fine || fine.r % 2 !== 0 || fine.c % 2 !== 0) return null;
        return { row: fine.r / 2, col: fine.c / 2 };
    }

    /**
     * The fine cursor as a cell, or null when it sits on an edge or intersection (or
     * the mode is off). The cell-centre sibling of `keyboardIntersection()`, for a
     * game whose pieces span two cell centres and whose trace runs cell to cell —
     * Ignition's fuses. Same use: the trace lands the cursor on the cell it arrived
     * at, so a piece grows away from it and retracts into it.
     */
    protected keyboardFineCell(): Position | null {
        const fine = this.keyboardFineCursor;
        if (!fine || fine.r % 2 !== 1 || fine.c % 2 !== 1) return null;
        return { row: (fine.r - 1) / 2, col: (fine.c - 1) / 2 };
    }

    /**
     * Whether a keyboard trace is live on the fine cursor. Games that draw something
     * *alongside* the gesture (Deluge's amber snap dots) gate on this, so it goes away
     * however the gesture ends — released, `X`, a game switch, or keyboard mode off.
     */
    protected isKeyboardFineTracing(): boolean {
        return !!this.keyboardFineCursor && this.keyboardCursorStyle === 'reticle';
    }

    /** Set/clear the intersection-point keyboard cursor (Line). */
    setKeyboardPointCursor(point: Position | null, style: KeyboardCursorStyle = 'crosshair'): void {
        this.keyboardPointCursor = point;
        if (point) this.keyboardCursorStyle = style;
    }

    /**
     * Keyboard cursor over the cursor cell plus ticks on the grid rim aligned to
     * its row and column. Blue with a crosshair in place mode, blue with a
     * corner-bracket reticle while tracing, red with an X during a delete sweep.
     * Games opt in to keyboard mode by calling this at the very end of their
     * render() (so the cursor sits on top); a no-op when unset.
     */
    protected drawKeyboardCursor(): void {
        if (this.keyboardFineCursor) { this.drawFineCursor(this.keyboardFineCursor); return; }
        if (this.keyboardPointCursor) { this.drawPointCursor(this.keyboardPointCursor); return; }
        const pos = this.keyboardCursor;
        if (!pos) return;

        const style = this.keyboardCursorStyle;
        const isDelete = style === 'delete';
        const tintName: keyof typeof COLORS = isDelete ? 'red' : 'blue';
        const color = COLORS[tintName];

        const { padding, cellSize } = this.config;
        const n = this.grid.size;

        // On the rim ring (one cell outside an edge), the cursor is a stem-style
        // triangle pointing inward rather than a cell crosshair.
        if (pos.row < 0 || pos.row >= n || pos.col < 0 || pos.col >= n) {
            this.drawStemCursor(pos, color);
            return;
        }

        const cx = padding + (pos.col + 0.5) * cellSize;
        const cy = padding + (pos.row + 0.5) * cellSize;
        const tl = this.getCellTopLeft(pos);

        this.ctx.save();

        // Trace hint: a segment from the previous cell into this one.
        if (this.keyboardCursorHintFrom) {
            const a = this.getCellCenter(this.keyboardCursorHintFrom);
            this.drawTraceHint(a.x, a.y, cx, cy);
        }

        // Faint fill + outline on the cursor cell (tinted by mode).
        this.ctx.fillStyle = colorWithAlpha(tintName, 0.12);
        this.ctx.fillRect(tl.x, tl.y, cellSize, cellSize);
        this.ctx.strokeStyle = colorWithAlpha(tintName, 0.9);
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(tl.x + 1, tl.y + 1, cellSize - 2, cellSize - 2);

        this.ctx.strokeStyle = color;
        this.ctx.lineCap = 'round';
        if (style === 'reticle') {
            this.drawReticle(tl.x, tl.y, cellSize, color);
        } else if (isDelete) {
            this.drawCrosshairX(cx, cy, cellSize);
        } else {
            this.drawCrosshairPlus(cx, cy, cellSize);
        }

        this.drawRimTicks(cx, cy, color);

        this.ctx.restore();
    }

    /** Trace hint: a translucent blue segment from the previous cursor point into
     *  the current one, with a small arrowhead at the midpoint showing the heading. */
    private drawTraceHint(ax: number, ay: number, bx: number, by: number): void {
        this.ctx.save();
        this.ctx.strokeStyle = colorWithAlpha('blue', 0.55);
        this.ctx.lineWidth = 2;
        this.ctx.lineCap = 'round';
        this.ctx.beginPath();
        this.ctx.moveTo(ax, ay);
        this.ctx.lineTo(bx, by);
        this.ctx.stroke();

        // Arrowhead at the midpoint, pointing from a → b.
        this.ctx.translate((ax + bx) / 2, (ay + by) / 2);
        this.ctx.rotate(Math.atan2(by - ay, bx - ax));
        this.ctx.fillStyle = colorWithAlpha('blue', 0.85);
        this.ctx.beginPath();
        this.ctx.moveTo(5, 0);
        this.ctx.lineTo(-4, -4);
        this.ctx.lineTo(-4, 4);
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.restore();
    }

    /** Short marks just outside the grid tracking the cursor's column (top/bottom)
     *  and row (left/right). Shared by the cell and fine cursors. */
    private drawRimTicks(x: number, y: number, color: string): void {
        const { padding, cellSize } = this.config;
        const gridPx = cellSize * this.grid.size;
        const tick = Math.min(10, padding - 2);
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 3;
        this.ctx.beginPath();
        this.ctx.moveTo(x, padding - tick); this.ctx.lineTo(x, padding);
        this.ctx.moveTo(x, padding + gridPx); this.ctx.lineTo(x, padding + gridPx + tick);
        this.ctx.moveTo(padding - tick, y); this.ctx.lineTo(padding, y);
        this.ctx.moveTo(padding + gridPx, y); this.ctx.lineTo(padding + gridPx + tick, y);
        this.ctx.stroke();
    }

    /**
     * Half-grid (Walls) cursor. A half-size crosshair on edges (an X while
     * deleting), a cell-size reticle on a cell center mid-trace, and a small dot
     * on centers/intersections otherwise. Twice-fine because walls live on edges.
     */
    private drawFineCursor(p: FinePoint): void {
        const style = this.keyboardCursorStyle;
        const isDelete = style === 'delete';
        const tintName: keyof typeof COLORS = isDelete ? 'red' : 'blue';
        const { padding, cellSize } = this.config;
        const x = padding + (p.c / 2) * cellSize;
        const y = padding + (p.r / 2) * cellSize;
        const onCenter = p.r % 2 === 1 && p.c % 2 === 1;
        const onIntersection = p.r % 2 === 0 && p.c % 2 === 0;
        const onEdge = (p.r % 2) !== (p.c % 2);
        // Top-rim slot row (Flood's faucet): the odd columns are the slots.
        const onSlot = this.keyboardFineTopRimSlots && p.r === 0 && p.c % 2 === 1;
        // A rim edge, on any of the four sides (Branches' stem). Same marker as the
        // slot row, but in place rather than sliding between slot midpoints.
        const rimCell = this.keyboardFineRimMarkers && !onSlot ? this.fineRimCell(p) : null;

        this.ctx.save();

        // Trace hint: a segment from the previous cell center into this one.
        if (this.keyboardFineHintFrom) {
            const h = this.keyboardFineHintFrom;
            this.drawTraceHint(padding + (h.c / 2) * cellSize, padding + (h.r / 2) * cellSize, x, y);
        }

        this.ctx.strokeStyle = COLORS[tintName];
        this.ctx.fillStyle = COLORS[tintName];
        this.ctx.lineCap = 'round';

        if (onSlot) {
            // A slot on the top rim (Flood's faucet): a bigger echo of the rim
            // marker, sliding along the boundary, rather than a crosshair.
            this.drawStemCursor({ row: -1, col: (p.c - 1) / 2 }, COLORS[tintName]);
        } else if (rimCell) {
            // A rim edge, where the piece is anchored to the boundary rather than
            // sitting on it (Branches' stem): the same inward-pointing marker.
            this.drawStemCursor(rimCell, COLORS[tintName]);
        } else if (style === 'reticle' && (onCenter || onIntersection)) {
            // Reticle marking the anchor: a full cell for centre traces; for the ones
            // that run along the lines, a tighter box (Flood) — or a wider one that
            // clears the digit target (Line's intersections).
            const box = onCenter ? cellSize : cellSize * (this.keyboardFinePointCursor ? 0.82 : 0.6);
            this.drawReticle(x - box / 2, y - box / 2, box, COLORS[tintName]);
        } else if (onEdge) {
            // Half-size crosshair (walls sit on edges, half a cell apart), over a
            // thick backing stroke where the board behind it is busy (Flood's water).
            const cross = (color: string, width: number) => {
                this.ctx.strokeStyle = color;
                if (isDelete) this.drawCrosshairX(x, y, cellSize * 0.5, width);
                else this.drawCrosshairPlus(x, y, cellSize * 0.5, width);
            };
            if (this.keyboardCursorHalo) cross(COLORS.background, 6);
            cross(COLORS[tintName], 2);
        } else if (this.keyboardFinePointCursor && onIntersection) {
            // Line: a wide-gap crosshair on the intersection, clearing the digit
            // target circle it sits over (a dot would be lost against it).
            this.drawWideCrosshair(x, y, isDelete, COLORS[tintName]);
        } else {
            // Center or intersection in place/delete mode: a small dot.
            if (this.keyboardCursorHalo) {
                this.ctx.fillStyle = COLORS.background;
                this.ctx.beginPath();
                this.ctx.arc(x, y, 5.5, 0, Math.PI * 2);
                this.ctx.fill();
                this.ctx.fillStyle = COLORS[tintName];
            }
            this.ctx.beginPath();
            this.ctx.arc(x, y, 3.5, 0, Math.PI * 2);
            this.ctx.fill();
        }

        this.drawRimTicks(x, y, COLORS[tintName]);

        this.ctx.restore();
    }

    /** A crosshair with a centre gap wide enough to clear a digit target circle, over
     *  an optional page-colour backing stroke. Used on intersections (Line). */
    private drawWideCrosshair(x: number, y: number, isDelete: boolean, color: string): void {
        const { cellSize } = this.config;
        const gap = cellSize * 0.24; // wide enough to clear the ~8px target circle
        const arm = cellSize * 0.42;
        const path = () => {
            this.ctx.beginPath();
            if (isDelete) {
                const d = Math.SQRT1_2; // diagonal arms (X)
                this.ctx.moveTo(x - arm * d, y - arm * d); this.ctx.lineTo(x - gap * d, y - gap * d);
                this.ctx.moveTo(x + gap * d, y + gap * d); this.ctx.lineTo(x + arm * d, y + arm * d);
                this.ctx.moveTo(x + arm * d, y - arm * d); this.ctx.lineTo(x + gap * d, y - gap * d);
                this.ctx.moveTo(x - gap * d, y + gap * d); this.ctx.lineTo(x - arm * d, y + arm * d);
            } else {
                this.ctx.moveTo(x - arm, y); this.ctx.lineTo(x - gap, y);
                this.ctx.moveTo(x + gap, y); this.ctx.lineTo(x + arm, y);
                this.ctx.moveTo(x, y - arm); this.ctx.lineTo(x, y - gap);
                this.ctx.moveTo(x, y + gap); this.ctx.lineTo(x, y + arm);
            }
        };
        if (this.keyboardCursorHalo) {
            path();
            this.ctx.strokeStyle = COLORS.background;
            this.ctx.lineWidth = 6;
            this.ctx.stroke();
        }
        path();
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 2;
        this.ctx.stroke();
    }

    /** Point cursor (Line): a crosshair/reticle/X centred on a grid intersection,
     *  sized with a wide centre gap so it clears the digit target circles it sits
     *  on, plus rim ticks tracking its row/column. */
    private drawPointCursor(p: Position): void {
        const style = this.keyboardCursorStyle;
        const isDelete = style === 'delete';
        const tintName: keyof typeof COLORS = isDelete ? 'red' : 'blue';
        const { padding, cellSize } = this.config;
        const x = padding + p.col * cellSize;
        const y = padding + p.row * cellSize;

        this.ctx.save();
        this.ctx.lineCap = 'round';

        if (style === 'reticle') {
            const box = cellSize * 0.82; // brackets sit outside the target circle
            this.drawReticle(x - box / 2, y - box / 2, box, COLORS[tintName]);
        } else {
            const gap = cellSize * 0.24; // wide enough to clear the ~8px target circle
            const arm = cellSize * 0.42;
            const path = () => {
                this.ctx.beginPath();
                if (isDelete) {
                    const d = Math.SQRT1_2; // diagonal arms (X)
                    this.ctx.moveTo(x - arm * d, y - arm * d); this.ctx.lineTo(x - gap * d, y - gap * d);
                    this.ctx.moveTo(x + gap * d, y + gap * d); this.ctx.lineTo(x + arm * d, y + arm * d);
                    this.ctx.moveTo(x + arm * d, y - arm * d); this.ctx.lineTo(x + gap * d, y - gap * d);
                    this.ctx.moveTo(x - gap * d, y + gap * d); this.ctx.lineTo(x - arm * d, y + arm * d);
                } else {
                    this.ctx.moveTo(x - arm, y); this.ctx.lineTo(x - gap, y);
                    this.ctx.moveTo(x + gap, y); this.ctx.lineTo(x + arm, y);
                    this.ctx.moveTo(x, y - arm); this.ctx.lineTo(x, y - gap);
                    this.ctx.moveTo(x, y + gap); this.ctx.lineTo(x, y + arm);
                }
            };

            // Backing stroke: a thicker line in the page colour, so the cursor still
            // reads against a busy fill (Flood's water) rather than dissolving into it.
            if (this.keyboardCursorHalo) {
                path();
                this.ctx.strokeStyle = COLORS.background;
                this.ctx.lineWidth = 6;
                this.ctx.stroke();
            }
            path();
            this.ctx.strokeStyle = COLORS[tintName];
            this.ctx.lineWidth = 2;
            this.ctx.stroke();
        }

        this.drawRimTicks(x, y, COLORS[tintName]);
        this.ctx.restore();
    }

    /** Rim cursor: an inward-pointing triangle on the grid edge (stem placement),
     *  sitting in the padding margin — a slightly larger echo of a rim marker. */
    /**
     * The cell just *outside* the grid for a fine point sitting on a rim edge, or
     * null anywhere else. That cell is what `drawStemCursor` aims its triangle from,
     * and it is also how the controller names a trace hop across the rim.
     */
    protected fineRimCell(p: FinePoint): Position | null {
        const hi = 2 * this.grid.size;
        if (p.r === 0 && p.c % 2 === 1) return { row: -1, col: (p.c - 1) / 2 };
        if (p.r === hi && p.c % 2 === 1) return { row: this.grid.size, col: (p.c - 1) / 2 };
        if (p.c === 0 && p.r % 2 === 1) return { row: (p.r - 1) / 2, col: -1 };
        if (p.c === hi && p.r % 2 === 1) return { row: (p.r - 1) / 2, col: this.grid.size };
        return null;
    }

    private drawStemCursor(pos: Position, color: string): void {
        const { padding, cellSize } = this.config;
        const n = this.grid.size;
        let x: number, y: number, angle: number;
        if (pos.row < 0) {            // top rim
            x = padding + (pos.col + 0.5) * cellSize; y = padding; angle = 0;
        } else if (pos.row >= n) {    // bottom rim
            x = padding + (pos.col + 0.5) * cellSize; y = padding + n * cellSize; angle = Math.PI;
        } else if (pos.col < 0) {     // left rim
            x = padding; y = padding + (pos.row + 0.5) * cellSize; angle = -Math.PI / 2;
        } else {                      // right rim
            x = padding + n * cellSize; y = padding + (pos.row + 0.5) * cellSize; angle = Math.PI / 2;
        }

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.moveTo(-12, -17); // base, in the padding margin
        this.ctx.lineTo(12, -17);
        this.ctx.lineTo(0, 0);     // tip on the rim line, pointing inward
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.restore();
    }

    /** Place-mode cursor: a plus with a center gap. */
    private drawCrosshairPlus(cx: number, cy: number, cellSize: number, width = 2): void {
        const arm = cellSize * 0.30;
        const gap = cellSize * 0.12;
        this.ctx.lineWidth = width;
        this.ctx.beginPath();
        this.ctx.moveTo(cx - arm, cy); this.ctx.lineTo(cx - gap, cy);
        this.ctx.moveTo(cx + gap, cy); this.ctx.lineTo(cx + arm, cy);
        this.ctx.moveTo(cx, cy - arm); this.ctx.lineTo(cx, cy - gap);
        this.ctx.moveTo(cx, cy + gap); this.ctx.lineTo(cx, cy + arm);
        this.ctx.stroke();
    }

    /** Delete-mode cursor: an X (diagonal crosshair) with a center gap. */
    private drawCrosshairX(cx: number, cy: number, cellSize: number, width = 2): void {
        const arm = cellSize * 0.26;
        const gap = cellSize * 0.12;
        const d = Math.SQRT1_2; // project arm/gap onto the diagonals
        this.ctx.lineWidth = width;
        this.ctx.beginPath();
        this.ctx.moveTo(cx - arm * d, cy - arm * d); this.ctx.lineTo(cx - gap * d, cy - gap * d);
        this.ctx.moveTo(cx + gap * d, cy + gap * d); this.ctx.lineTo(cx + arm * d, cy + arm * d);
        this.ctx.moveTo(cx + arm * d, cy - arm * d); this.ctx.lineTo(cx + gap * d, cy - gap * d);
        this.ctx.moveTo(cx - gap * d, cy + gap * d); this.ctx.lineTo(cx - arm * d, cy + arm * d);
        this.ctx.stroke();
    }

    /** Trace-mode cursor: four L-shaped corner brackets inside the cell. */
    /** Reticle brackets in `color`, over a thick backing stroke when the board behind
     *  the cursor is busy enough to swallow a thin line. */
    private drawReticle(x: number, y: number, size: number, color: string): void {
        if (this.keyboardCursorHalo) {
            this.ctx.strokeStyle = COLORS.background;
            this.drawReticleBrackets(x, y, size, 6);
        }
        this.ctx.strokeStyle = color;
        this.drawReticleBrackets(x, y, size, 2);
    }

    private drawReticleBrackets(x: number, y: number, cellSize: number, width = 2): void {
        const inset = cellSize * 0.20;
        const len = cellSize * 0.22;
        const l = x + inset, r = x + cellSize - inset;
        const t = y + inset, b = y + cellSize - inset;
        this.ctx.lineWidth = width;
        this.ctx.beginPath();
        // top-left
        this.ctx.moveTo(l, t + len); this.ctx.lineTo(l, t); this.ctx.lineTo(l + len, t);
        // top-right
        this.ctx.moveTo(r - len, t); this.ctx.lineTo(r, t); this.ctx.lineTo(r, t + len);
        // bottom-right
        this.ctx.moveTo(r, b - len); this.ctx.lineTo(r, b); this.ctx.lineTo(r - len, b);
        // bottom-left
        this.ctx.moveTo(l + len, b); this.ctx.lineTo(l, b); this.ctx.lineTo(l, b - len);
        this.ctx.stroke();
    }

    getGridSize(): number {
        return this.grid.size;
    }

    protected drawGrid(): void {
        const { padding, cellSize, gridLineColor, gridLineWidth } = this.config;
        const gridSize = this.grid.size;
        const totalSize = cellSize * gridSize;

        this.ctx.strokeStyle = gridLineColor;
        this.ctx.lineWidth = gridLineWidth;

        // Draw vertical lines
        for (let i = 0; i <= gridSize; i++) {
            const x = padding + i * cellSize;
            this.ctx.beginPath();
            this.ctx.moveTo(x, padding);
            this.ctx.lineTo(x, padding + totalSize);
            this.ctx.stroke();
        }

        // Draw horizontal lines
        for (let i = 0; i <= gridSize; i++) {
            const y = padding + i * cellSize;
            this.ctx.beginPath();
            this.ctx.moveTo(padding, y);
            this.ctx.lineTo(padding + totalSize, y);
            this.ctx.stroke();
        }

        // Draw thicker border
        this.ctx.strokeStyle = COLORS.borderGray;
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(padding, padding, totalSize, totalSize);
    }

    protected drawDigits(grid: Grid): void {
        const { digitColor, digitFont } = this.config;

        this.ctx.font = digitFont;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillStyle = digitColor;

        for (const [digit, pos] of grid.getAllDigitPositions()) {
            const center = this.getCellCenter(pos);
            this.ctx.fillText(digit.toString(), center.x, center.y + 2);
        }
    }

    /**
     * Remove the spans of axis-aligned line pieces that lie over a digit's cell,
     * leaving a gap there (so a beam/signal reads as passing behind the digit, its
     * passage marked by edge triangles instead). `inset` shrinks the clip box so
     * the gap is a touch tighter than the full cell.
     */
    protected clipSegmentsAtDigits(
        segments: Array<CanvasSeg>,
        inset: number = 7
    ): Array<CanvasSeg> {
        const { padding, cellSize } = this.config;
        const rects = [];
        for (const [, pos] of this.grid.getAllDigitPositions()) {
            const left = padding + pos.col * cellSize + inset;
            const top = padding + pos.row * cellSize + inset;
            rects.push({ left, top, right: left + cellSize - 2 * inset, bottom: top + cellSize - 2 * inset });
        }

        const out: Array<CanvasSeg> = [];
        for (const seg of segments) {
            const dx = seg.to.x - seg.from.x;
            const dy = seg.to.y - seg.from.y;

            // t-intervals where the piece lies inside a digit cell
            const excluded: Array<[number, number]> = [];
            for (const r of rects) {
                const tx = this.slabInterval(seg.from.x, dx, r.left, r.right);
                const ty = this.slabInterval(seg.from.y, dy, r.top, r.bottom);
                if (!tx || !ty) continue;
                const lo = Math.max(tx[0], ty[0], 0);
                const hi = Math.min(tx[1], ty[1], 1);
                if (lo < hi) excluded.push([lo, hi]);
            }

            if (excluded.length === 0) { out.push(seg); continue; }

            excluded.sort((a, b) => a[0] - b[0]);
            const pt = (t: number) => ({ x: seg.from.x + dx * t, y: seg.from.y + dy * t });
            let cursor = 0;
            for (const [a, b] of excluded) {
                if (a > cursor) out.push({ from: pt(cursor), to: pt(a) });
                cursor = Math.max(cursor, b);
            }
            if (cursor < 1) out.push({ from: pt(cursor), to: pt(1) });
        }
        return out;
    }

    /** Stroke a straight line in the current ctx style, gapped over any digit cells. */
    protected strokeLineClippedAtDigits(
        from: { x: number; y: number }, to: { x: number; y: number }, inset: number = 7
    ): void {
        for (const seg of this.clipSegmentsAtDigits([{ from, to }], inset)) {
            this.ctx.beginPath();
            this.ctx.moveTo(seg.from.x, seg.from.y);
            this.ctx.lineTo(seg.to.x, seg.to.y);
            this.ctx.stroke();
        }
    }

    /** Parametric interval [t0,t1] where from + t·d stays within [lo,hi]; null if never. */
    private slabInterval(from: number, d: number, lo: number, hi: number): [number, number] | null {
        if (d === 0) return (from >= lo && from <= hi) ? [-Infinity, Infinity] : null;
        const t1 = (lo - from) / d;
        const t2 = (hi - from) / d;
        return [Math.min(t1, t2), Math.max(t1, t2)];
    }

    /**
     * Highlight a cell with a semi-transparent color.
     */
    protected highlightCell(pos: Position, color: string): void {
        const topLeft = this.getCellTopLeft(pos);
        this.ctx.fillStyle = color;
        this.ctx.fillRect(topLeft.x, topLeft.y, this.config.cellSize, this.config.cellSize);
    }

    /**
     * Draw a rounded square marker centered in a cell.
     */
    protected drawRoundedSquare(pos: Position, color: string, inset: number, filled: boolean = true): void {
        const { cellSize } = this.config;
        const topLeft = this.getCellTopLeft(pos);
        const x = topLeft.x + inset;
        const y = topLeft.y + inset;
        const size = cellSize - inset * 2;
        const radius = size * 0.2;

        this.ctx.beginPath();
        if (filled) {
            this.ctx.fillStyle = color;
            this.ctx.roundRect(x, y, size, size, radius);
            this.ctx.fill();
        } else {
            this.ctx.strokeStyle = color;
            this.ctx.lineWidth = 3;
            this.ctx.roundRect(x + 1.5, y + 1.5, size - 3, size - 3, radius);
            this.ctx.stroke();
        }
    }

    // ── Cell replay (docs/juice.md) ───────────────────────────────────
    //
    // Games whose simulated path is drawn as long runs across the board can't
    // animate one cell's worth of it in place. These two do it between them: clip
    // the cell out of the normal drawing, then re-draw what crosses it as two
    // halves — in from the edge it enters, out to the edge it leaves — cut to how
    // far the replay has got.

    /**
     * Clip to everything *except* these cells, so the caller can redraw whatever
     * falls inside them. Wrap the normal drawing in `save()`/`restore()` around it.
     */
    protected clipOutCells(cells: Position[]): void {
        const { cellSize } = this.config;
        const path = new Path2D();
        path.rect(0, 0, this.logicalSize, this.logicalSize);
        for (const cell of cells) {
            const topLeft = this.getCellTopLeft(cell);
            path.rect(topLeft.x, topLeft.y, cellSize, cellSize);
        }
        this.ctx.clip(path, 'evenodd');   // the inner rects punch holes
    }

    /**
     * A straight-line crossing of `cell`, entering from the `inDir` edge and leaving
     * by the `outDir` edge, cut to a replay `p` of 0..1: the in-half draws over the
     * first half of the progress and the out-half over the second, so the two run in
     * travel order. Returns the pieces to stroke, or none where nothing shows yet.
     */
    protected crossingHalves(
        cell: Position, inDir: Direction, outDir: Direction, p: number
    ): Array<[{ x: number; y: number }, { x: number; y: number }]> {
        const half = this.config.cellSize / 2;
        const c = this.getCellCenter(cell);
        const inV = GridRenderer.DIR_VEC[inDir];
        const outV = GridRenderer.DIR_VEC[outDir];
        return this.crossingPieces(
            cell,
            { x: c.x - inV.x * half, y: c.y - inV.y * half },
            { x: c.x + outV.x * half, y: c.y + outV.y * half },
            p
        );
    }

    /**
     * As above, but from explicit entry/exit points — for a path that starts or ends
     * inside the cell (a launcher on its edge, an exit off the board) rather than
     * running clean through. A half given as null doesn't exist, and the other then
     * takes the whole of `p` rather than half of it.
     */
    protected crossingPieces(
        cell: Position,
        entry: { x: number; y: number } | null,
        exit: { x: number; y: number } | null,
        p: number
    ): Array<[{ x: number; y: number }, { x: number; y: number }]> {
        const c = this.getCellCenter(cell);
        const lerp = (a: { x: number; y: number }, b: { x: number; y: number }, t: number) =>
            ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
        const pieces: Array<[{ x: number; y: number }, { x: number; y: number }]> = [];

        if (entry && exit) {
            const inT = Math.min(1, p * 2);
            const outT = Math.max(0, p * 2 - 1);
            if (inT > 0) pieces.push([entry, lerp(entry, c, inT)]);
            if (outT > 0) pieces.push([c, lerp(c, exit, outT)]);
        } else if (entry && p > 0) {
            pieces.push([entry, lerp(entry, c, p)]);
        } else if (exit && p > 0) {
            pieces.push([c, lerp(c, exit, p)]);
        }
        return pieces;
    }

    /** Unit step per direction; screen y grows downward. */
    protected static readonly DIR_VEC: Record<Direction, { x: number; y: number }> = {
        E: { x: 1, y: 0 },
        W: { x: -1, y: 0 },
        N: { x: 0, y: -1 },
        S: { x: 0, y: 1 },
    };

    /**
     * Draw a thick border around a cell.
     */
    protected drawCellBorder(pos: Position, color: string, inset: number = 2): void {
        const topLeft = this.getCellTopLeft(pos);
        const { cellSize } = this.config;

        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 3;
        this.ctx.strokeRect(
            topLeft.x + inset,
            topLeft.y + inset,
            cellSize - inset * 2,
            cellSize - inset * 2
        );
    }

    /**
     * Draw a small triangle on one edge of a cell, marking where a path crosses it.
     * `travelDir` is the direction of travel; `inbound` points the triangle into the
     * cell across its entry edge, `outbound` points it out across the exit edge.
     * Shared by the path games (Walls, Orbits, …) so their edge markers match.
     */
    protected drawEdgeTriangle(
        cellPos: Position, travelDir: Direction, color: string, inbound: boolean, size: number = 5
    ): void {
        const { padding, cellSize } = this.config;
        const center = this.getCellCenter(cellPos);

        // The edge the triangle sits on: entry edge is opposite travel, exit edge is
        // along it. `angle` rotates the upward-pointing shape to face travel.
        let x = center.x, y = center.y, angle = 0;
        const near = inbound;  // inbound marks the trailing edge, outbound the leading one
        switch (travelDir) {
            case 'E': x = padding + (cellPos.col + (near ? 0 : 1)) * cellSize; angle = -Math.PI / 2; break;
            case 'W': x = padding + (cellPos.col + (near ? 1 : 0)) * cellSize; angle = Math.PI / 2; break;
            case 'S': y = padding + (cellPos.row + (near ? 0 : 1)) * cellSize; angle = 0; break;
            case 'N': y = padding + (cellPos.row + (near ? 1 : 0)) * cellSize; angle = Math.PI; break;
        }

        const tip = size * 1.8;
        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        if (inbound) {
            this.ctx.moveTo(-size, 0.2 * size);
            this.ctx.lineTo(size, 0.2 * size);
            this.ctx.lineTo(0, tip);
        } else {
            this.ctx.moveTo(-size, -tip);
            this.ctx.lineTo(size, -tip);
            this.ctx.lineTo(0, -0.2 * size);
        }
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.restore();
    }

    /**
     * Draw a rim marker (entry/exit triangle) on the grid edge.
     * Uses transforms for clean rotation based on edge detection.
     *
     * @param position - Cell position (row 0 = top row of cells, col 0 = left column)
     * @param color - Fill color for the marker
     * @param entry - true = pointing into grid, false = pointing out
     * @param withDrop - Adds water drop below marker (for faucets)
     * @param isGridPoint - true if position is a grid intersection Point, false for cell Position
     * @param edge - Which edge the marker is on: 'top' | 'bottom' | 'left' | 'right'
     */
    protected drawRimMarker(options: {
        position: Position | Point,
        color: string,
        entry: boolean,
        withDrop?: boolean,
        isGridPoint?: boolean,
        edge?: 'top' | 'bottom' | 'left' | 'right'
    }): void {
        const { position, color, entry, withDrop, isGridPoint, edge } = options;
        const { padding, cellSize } = this.config;
        const gridSize = this.grid.size;

        let x: number, y: number;
        let angle: number;

        const row = position.row;
        const col = position.col;

        if (isGridPoint) {
            // Grid intersection point - use coordinates directly
            x = padding + col * cellSize;
            y = padding + row * cellSize;

            // Determine edge from point location
            const actualEdge = edge ?? (
                row === 0 ? 'top' :
                row === gridSize ? 'bottom' :
                col === 0 ? 'left' : 'right'
            );

            switch (actualEdge) {
                case 'top':
                    angle = entry ? 0 : Math.PI;
                    break;
                case 'bottom':
                    angle = entry ? Math.PI : 0;
                    break;
                case 'left':
                    angle = entry ? -Math.PI / 2 : Math.PI / 2;
                    break;
                case 'right':
                    angle = entry ? Math.PI / 2 : -Math.PI / 2;
                    break;
            }
        } else {
            // Cell position - center on the cell at the specified edge
            const actualEdge = edge ?? (
                row === 0 ? 'top' :
                row === gridSize - 1 ? 'bottom' :
                col === 0 ? 'left' : 'right'
            );

            switch (actualEdge) {
                case 'top':
                    x = padding + col * cellSize + cellSize / 2;
                    y = padding;
                    angle = entry ? 0 : Math.PI;
                    break;
                case 'bottom':
                    x = padding + col * cellSize + cellSize / 2;
                    y = padding + gridSize * cellSize;
                    angle = entry ? Math.PI : 0;
                    break;
                case 'left':
                    x = padding;
                    y = padding + row * cellSize + cellSize / 2;
                    angle = entry ? -Math.PI / 2 : Math.PI / 2;
                    break;
                case 'right':
                    x = padding + gridSize * cellSize;
                    y = padding + row * cellSize + cellSize / 2;
                    angle = entry ? Math.PI / 2 : -Math.PI / 2;
                    break;
            }
        }

        // Draw triangle using transforms (pointing down at angle 0)
        // Triangle: base 20px, height 15px (matching Flood faucet)
        // For exit markers, offset outward so triangle is outside the grid
        const outwardOffset = entry ? 0 : 17;

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);
        // Apply outward offset (in rotated coordinates, "down" is outward for exits)
        this.ctx.translate(0, outwardOffset);

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.moveTo(-10, -15); // Top-left of base
        this.ctx.lineTo(10, -15);  // Top-right of base
        this.ctx.lineTo(0, 0);     // Tip pointing down (at angle 0)
        this.ctx.closePath();
        this.ctx.fill();

        // Draw water drop if requested (below the triangle tip)
        if (withDrop) {
            this.ctx.beginPath();
            this.ctx.arc(0, 8, 4, 0, Math.PI * 2);
            this.ctx.fill();
        }

        this.ctx.restore();
    }

    /**
     * Draw a badge (ring with number) at a position relative to a cell.
     * Badges are colored rings with same-colored numbers inside.
     *
     * @param filled - If true, fills the ring interior with background color for legibility
     */
    protected drawBadge(
        pos: Position,
        quadrant: BadgeQuadrant,
        content: string,
        color: string,
        size: 'xs' | 'sm' | 'md' | 'lg' = 'sm',
        filled: boolean = false
    ): void {
        const { cellSize } = this.config;
        const center = this.getCellCenter(pos);

        // Determine radius based on size
        const radius = size === 'xs' ? 5 : size === 'sm' ? 9 : size === 'md' ? 11 : 12;
        const ringWidth = size === 'xs' ? 2 : 3;

        // Calculate offset based on quadrant
        const diagonalOffset = cellSize * 0.35;
        const cardinalOffset = cellSize * 0.45;
        let badgeX = center.x;
        let badgeY = center.y; // Slight vertical adjustment for optical centering

        switch (quadrant) {
            case 'N':
                badgeY = center.y - cardinalOffset;
                break;
            case 'S':
                badgeY = center.y + cardinalOffset;
                break;
            case 'E':
                badgeX = center.x + cardinalOffset;
                break;
            case 'W':
                badgeX = center.x - cardinalOffset;
                break;
            case 'NE':
                badgeX = center.x + diagonalOffset;
                badgeY = center.y - diagonalOffset;
                break;
            case 'SE':
                badgeX = center.x + diagonalOffset;
                badgeY = center.y + diagonalOffset;
                break;
            case 'SW':
                badgeX = center.x - diagonalOffset;
                badgeY = center.y + diagonalOffset;
                break;
            case 'NW':
                badgeX = center.x - diagonalOffset;
                badgeY = center.y - diagonalOffset;
                break;
        }

        // Draw background fill if requested (for legibility over busy backgrounds)
        if (filled) {
            this.ctx.fillStyle = COLORS.background;
            this.ctx.beginPath();
            this.ctx.arc(badgeX, badgeY, radius, 0, Math.PI * 2);
            this.ctx.fill();
        }

        // Draw ring
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = ringWidth;
        this.ctx.beginPath();
        this.ctx.arc(badgeX, badgeY, radius, 0, Math.PI * 2);
        this.ctx.stroke();

        // Draw content text in the same color (skip if empty)
        if (content) {
            const textOffset = size === 'lg' ? 1 : 0; // Adjust for larger sizes
            this.ctx.fillStyle = color;
            this.ctx.font = canvasFont(size === 'lg' ? 10 : 11);
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText(content, badgeX, badgeY + textOffset); // Slight vertical adjustment for optical centering
        }
    }

    // ── Shared Vortex Rendering ────────────────────────────────────

    /**
     * Draw purple rounded squares on active Vortex gates.
     */
    protected drawActiveVortexGates(game: IVortexGame): void {
        const activeGates = game.getActiveGatePairs();
        if (activeGates.size === 0) return;

        const inset = this.config.cellSize * 0.16;
        const drawn = new Set<string>();
        for (const [key] of activeGates) {
            if (drawn.has(key)) continue;
            drawn.add(key);
            this.drawRoundedSquare(parsePositionKey(key), COLORS.purple, inset);
        }
    }

    /**
     * Draw dashed purple lines between paired active Vortex gates.
     */
    protected drawVortexGateDashedLines(game: IVortexGame): void {
        const activeGates = game.getActiveGatePairs();
        if (activeGates.size === 0) return;

        this.ctx.strokeStyle = COLORS.purple;
        this.ctx.lineWidth = 3;
        this.ctx.setLineDash([6, 4]);
        this.ctx.lineCap = 'round';
        this.ctx.globalAlpha = 0.6;

        const drawn = new Set<string>();
        for (const [key, pairedKey] of activeGates) {
            const pairId = [key, pairedKey].sort().join('|');
            if (drawn.has(pairId)) continue;
            drawn.add(pairId);

            const from = this.getCellCenter(parsePositionKey(key));
            const to = this.getCellCenter(parsePositionKey(pairedKey));
            this.ctx.beginPath();
            this.ctx.moveTo(from.x, from.y);
            this.ctx.lineTo(to.x, to.y);
            this.ctx.stroke();
        }

        this.ctx.setLineDash([]);
        this.ctx.globalAlpha = 1.0;
    }

    /**
     * Draw highlights on cells where gates can be activated or are active (gate mode).
     * Purple for activatable/active gates, amber for paired but non-activatable gates.
     * Also draws dashed lines between each pair.
     */
    protected drawGateModeHighlights(game: IVortexGame): void {
        // Fade the whole canvas (incl. off-grid elements) so the gate overlay reads on top
        this.ctx.fillStyle = colorWithAlpha('background', 0.6);
        this.ctx.fillRect(0, 0, this.logicalSize, this.logicalSize);

        // Pending first gate of a new pair — shown even before any gate exists
        if (this.gatePendingCreateCell) {
            const inset = this.config.cellSize * 0.16;
            this.highlightCell(this.gatePendingCreateCell, colorWithAlpha('purple', 0.25));
            this.drawRoundedSquare(this.gatePendingCreateCell, COLORS.purple, inset, false);
        }

        const allGates = game.getAllGatePositions();
        if (allGates.size === 0) return;

        // Build set of paired gate keys for quick lookup
        const pairs = game.getVortexGatePairs();
        const pairedKeys = new Set<string>();
        for (const [a, b] of pairs) {
            pairedKeys.add(positionKey(a));
            pairedKeys.add(positionKey(b));
        }

        // Highlight each gate
        for (const gateKey of allGates) {
            const pos = parsePositionKey(gateKey);
            if (game.canActivateGate(pos) || game.isActiveGate(pos)) {
                this.highlightCell(pos, colorWithAlpha('purple', 0.25));
                this.drawCellBorder(pos, COLORS.purple);
            } else if (pairedKeys.has(gateKey)) {
                this.highlightCell(pos, colorWithAlpha('amber', 0.25));
                this.drawCellBorder(pos, COLORS.amber);
            }
        }

        // Draw a line between each pair: solid if linked in the Hub, dashed if not.
        this.ctx.lineWidth = 4;
        this.ctx.lineCap = 'round';
        this.ctx.globalAlpha = 0.6;

        for (const [a, b] of pairs) {
            const aKey = positionKey(a);
            const bKey = positionKey(b);
            const aActivatable = game.canActivateGate(a) || game.isActiveGate(a);
            const bActivatable = game.canActivateGate(b) || game.isActiveGate(b);
            this.ctx.strokeStyle = (aActivatable || bActivatable) ? COLORS.purple : COLORS.amber;

            const pairId = aKey < bKey ? `${aKey}|${bKey}` : `${bKey}|${aKey}`;
            this.ctx.setLineDash(this.linkedGatePairIds.has(pairId) ? [] : [8, 6]);

            const from = this.getCellCenter(a);
            const to = this.getCellCenter(b);
            this.ctx.beginPath();
            this.ctx.moveTo(from.x, from.y);
            this.ctx.lineTo(to.x, to.y);
            this.ctx.stroke();
        }

        this.ctx.setLineDash([]);
        this.ctx.globalAlpha = 1.0;
    }

    /**
     * Scoring-rules overlay: fade the board (same as the gate-mode overlay), tint
     * secondary (additive) elements blue, then redraw the primary (×100) pieces in
     * amber on top via {@link drawScoringPrimary}. Cell highlights are borderless.
     * Call after the game has rendered.
     */
    drawScoringOverlay(game: IGame, highlight: ScoringHighlight): void {
        const { padding, cellSize } = this.config;
        this.ctx.save();
        this.ctx.fillStyle = colorWithAlpha('background', 0.62);
        // Cover the whole canvas, including off-grid elements (rim fuses, exit gates…).
        this.ctx.fillRect(0, 0, this.logicalSize, this.logicalSize);

        const pointPx = (p: Point) => ({ x: padding + p.col * cellSize, y: padding + p.row * cellSize });
        const drawEdges = (edges: Array<[Point, Point]> | undefined, color: string) => {
            if (!edges?.length) return;
            this.ctx.strokeStyle = color;
            this.ctx.lineWidth = Math.max(4, cellSize * 0.12);
            this.ctx.lineCap = 'round';
            for (const [a, b] of edges) {
                const pa = pointPx(a), pb = pointPx(b);
                this.ctx.beginPath();
                this.ctx.moveTo(pa.x, pa.y);
                this.ctx.lineTo(pb.x, pb.y);
                this.ctx.stroke();
            }
        };

        // Secondary tints (blue), borderless. Primary cells/edges are a generic
        // fallback; renderers that can redraw their pieces do so in drawScoringPrimary.
        const drawDots = (pts: Point[] | undefined, color: string) => {
            if (!pts?.length) return;
            this.ctx.fillStyle = color;
            const r = Math.max(3, cellSize * 0.1);
            for (const p of pts) {
                const px = pointPx(p);
                this.ctx.beginPath();
                this.ctx.arc(px.x, px.y, r, 0, Math.PI * 2);
                this.ctx.fill();
            }
        };

        for (const c of highlight.secondaryCells ?? []) this.highlightCell(c, colorWithAlpha('blue', 0.4));
        for (const c of highlight.primaryCells ?? []) this.highlightCell(c, colorWithAlpha('amber', 0.4));
        drawEdges(highlight.secondaryEdges, COLORS.blue);
        drawEdges(highlight.primaryEdges, COLORS.amber);

        this.drawScoringPrimary(game);

        drawDots(highlight.secondaryPoints, COLORS.blue);
        drawDots(highlight.primaryPoints, COLORS.amber);
        if (highlight.spreadArc) this.drawSpreadArc(highlight.spreadArc);

        this.ctx.restore();
    }

    /**
     * Redraw a game's primary (×100) scored pieces in amber, on top of the faded
     * board. Default no-op; each game renderer overrides to re-render its pieces
     * (mirrors, walls, gates, …) in {@link COLORS.amber}.
     */
    protected drawScoringPrimary(_game: IGame): void { /* overridden per game */ }

    /**
     * Spread highlight: redraw the two digits' value badges (NE corner) and join
     * them with a curved arc between the badges. amber for a primary (×100) spread,
     * blue for a secondary (additive) one.
     */
    private drawSpreadArc(arc: NonNullable<ScoringHighlight['spreadArc']>): void {
        const { cellSize } = this.config;
        const key = arc.primary ? 'amber' : 'blue';

        // Badge anchor = NE diagonal offset from the cell center (matches drawBadge('NE')).
        const off = cellSize * 0.35;
        const anchor = (pos: Position) => {
            const c = this.getCellCenter(pos);
            return { x: c.x + off, y: c.y - off };
        };
        const a = anchor(arc.from);
        const b = anchor(arc.to);

        const dx = b.x - a.x, dy = b.y - a.y;
        const len = Math.hypot(dx, dy) || 1;
        const bow = Math.min(len * 0.28, cellSize * 1.2);
        const cx = (a.x + b.x) / 2 - (dy / len) * bow;
        const cy = (a.y + b.y) / 2 + (dx / len) * bow;

        this.ctx.strokeStyle = COLORS[key];
        this.ctx.lineWidth = Math.max(3, cellSize * 0.07);
        this.ctx.lineCap = 'round';
        this.ctx.beginPath();
        this.ctx.moveTo(a.x, a.y);
        this.ctx.quadraticCurveTo(cx, cy, b.x, b.y);
        this.ctx.stroke();

        // Redraw the two digit badges (filled for legibility over the arc), at the
        // game's native badge size so they don't resize when the overlay appears.
        const badgeSize = arc.badgeSize ?? 'md';
        this.drawBadge(arc.from, 'NE', String(arc.fromValue), COLORS[key], badgeSize, true);
        this.drawBadge(arc.to, 'NE', String(arc.toValue), COLORS[key], badgeSize, true);
    }
}
